import React, { useState, useEffect, useMemo, useCallback } from 'react';

const TAGLINES = [
  "Emerald Horizon: See the Perfection Before We Start.",
  "Your Vision, Visualized. Your Reality, Redefined.",
  "Don't Just Imagine Your Dream Home—Preview It.",
  "Design with Certainty. Live in Luxury.",
  "A New Horizon in Modern Living.",
  "Seamless Elegance, Inside and Out.",
  "Where Artisan Craft Meets Functional Luxury.",
  "Emerald Horizon: Masterpieces Made to Order.",
  "Visualize. Renovate. Elevate.",
  "Bespoke Design. Timeless Finish.",
  "Crafting Your Horizon."
];

const GALLERY_IMAGES = [
  { src: 'white-gold-marble-media-wall.png', alt: 'White and gold marble media wall with fireplace' },
  { src: 'blue-gold-marble-bathroom.png', alt: 'Luxury blue and gold marble effect bathroom' },
  { src: 'pastel-kitchen-island.png', alt: 'Pastel marble kitchen with matching island and floor' },
  { src: 'marble-resin-living-room.png', alt: 'Luxury white and gold marble resin floor in modern living room' },
  { src: 'gold-velvet-media-wall.png', alt: 'Gold velvet effect media wall with fireplace and LED lighting' },
  { src: 'rainbow-resin-floor.png', alt: 'Rainbow pastel epoxy resin floor in open plan living space' },
  { src: 'luxury-white-gold-salon.png', alt: 'Luxury white and gold marble salon interior' },
  { src: 'blue-gold-marble-media-wall.png', alt: 'Blue and gold marble media wall with fireplace' },
  { src: 'Firefly_white and gold marble resin floor 153802.jpg', alt: 'White and gold marble resin floor' },
  { src: 'symmetrical-marble-media-wall.png', alt: 'Symmetrical marble media wall with fireplace and oak shelving' },
  { src: 'pastel-resin-floor.png', alt: 'Pastel pink and blue swirl epoxy resin floor' },
  { src: 'grey-marble-media-wall.png', alt: 'Grey marble media wall with integrated fireplace and LED shelving' },
  { src: 'luxury-pink-gold-salon.png', alt: 'Luxury pink and gold marble salon interior' },
  { src: 'blue-gold-geode-floor.png', alt: 'Blue and gold geode effect epoxy resin floor with matching feature wall' },
  { src: 'cream-plaster-media-wall.png', alt: 'Cream plaster media wall with LED niches and fireplace' },
  { src: 'Firefly_blue and gold resin marble effect bathroom 137074.jpg', alt: 'Blue and gold resin marble effect bathroom' },
  { src: 'white-gold-marble-living.png', alt: 'White and gold marble effect floor in open plan living space' },
  { src: 'white-gold-media-wall-2.png', alt: 'White and gold marble media wall with walnut shelf' },
  { src: 'John_Kenny_luxury_white_and_gold_marble_floor_and_countertop_in_a_kitchen_c9a87d2c-ad54-4cd2-9957-1581df2f7a3e.jpg', alt: 'Luxury white and gold marble floor and countertop in a kitchen' },
  { src: 'WhatsApp Image 2025-10-07 at 22.03.25.jpeg', alt: 'Project showcase' },
  { src: 'marble-kitchen-island.png', alt: 'White and gold marble effect floor with matching kitchen island' },
  { src: 'WhatsApp Image 2025-10-10 at 17.18.36.png', alt: 'Project showcase' },
  { src: 'WhatsApp Image 2025-10-10 at 17.18.36(5).png', alt: 'Project showcase' },
  { src: 'WhatsApp Image 2025-10-10 at 17.18.36(6).png', alt: 'Project showcase' },
].map((item) => ({
  ...item,
  url: process.env.PUBLIC_URL + '/images/' + encodeURIComponent(item.src),
}));

const INTERVAL_MS = 4500;

function Slideshow() {
  const [index, setIndex] = useState(0);
  const [expandedImage, setExpandedImage] = useState(null);
  const tagline = useMemo(() => TAGLINES[Math.floor(Math.random() * TAGLINES.length)], []);

  useEffect(() => {
    if (expandedImage !== null) return;
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % GALLERY_IMAGES.length);
    }, INTERVAL_MS);
    return () => clearInterval(id);
  }, [expandedImage]);

  const goTo = (i) => setIndex(i);
  const handleImageClick = (i) => setExpandedImage(i);
  const closeExpanded = useCallback(() => setExpandedImage(null), []);

  const goPrev = useCallback(() => {
    setExpandedImage((i) => (i - 1 + GALLERY_IMAGES.length) % GALLERY_IMAGES.length);
  }, []);

  const goNext = useCallback(() => {
    setExpandedImage((i) => (i + 1) % GALLERY_IMAGES.length);
  }, []);

  // Keyboard navigation for expanded overlay
  useEffect(() => {
    if (expandedImage === null) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') closeExpanded();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [expandedImage, closeExpanded, goPrev, goNext]);

  return (
    <section id="showcase" className="slideshow">
      <div className="section-inner">
        <h2 className="section-title">
          <span className="title-emerald">Need</span>{' '}
          <span className="title-gold">Inspiration?</span>
        </h2>
        <p className="slideshow-tagline">{tagline}</p>
        <div
          className="slideshow-container"
          role="region"
          aria-label="Image slideshow"
          aria-roledescription="carousel"
        >
          <div
            className="slideshow-track"
            aria-live="off"
            style={{ transform: `translateX(-${index * 100}%)` }}
          >
            {GALLERY_IMAGES.map((img, i) => (
              <button
                key={i}
                type="button"
                className="slideshow-slide"
                onClick={() => handleImageClick(i)}
                aria-label={`${img.alt}. Click to expand.`}
                tabIndex={i === index ? 0 : -1}
              >
                <img src={img.url} alt="" loading={i === 0 ? 'eager' : 'lazy'} />
                <div className="slideshow-slide-hint" aria-hidden="true">
                  <span>Click to expand</span>
                </div>
              </button>
            ))}
          </div>
          <div className="slideshow-dots" role="tablist" aria-label="Slideshow navigation">
            {GALLERY_IMAGES.map((_, i) => (
              <button
                key={i}
                type="button"
                role="tab"
                aria-selected={i === index}
                className={`slideshow-dot ${i === index ? 'active' : ''}`}
                aria-label={`Go to slide ${i + 1} of ${GALLERY_IMAGES.length}`}
                onClick={() => goTo(i)}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Expanded Image Overlay */}
      {expandedImage !== null && (
        <div
          className="slideshow-expanded-overlay"
          role="dialog"
          aria-modal="true"
          aria-label={GALLERY_IMAGES[expandedImage].alt}
          onClick={closeExpanded}
        >
          <div className="slideshow-expanded-container" onClick={(e) => e.stopPropagation()}>
            <img
              src={GALLERY_IMAGES[expandedImage].url}
              alt={GALLERY_IMAGES[expandedImage].alt}
              className="slideshow-expanded-image"
            />
            <button
              className="slideshow-expanded-close"
              onClick={closeExpanded}
              aria-label="Close expanded image"
              autoFocus
            >
              ×
            </button>
            <div className="slideshow-expanded-nav">
              <button
                className="slideshow-expanded-prev"
                onClick={goPrev}
                aria-label="Previous image"
              >
                ‹
              </button>
              <button
                className="slideshow-expanded-next"
                onClick={goNext}
                aria-label="Next image"
              >
                ›
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default Slideshow;
